import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Layout from '../components/Layout';
import ShareModal from '../components/ShareModal';
import SaveToAlbumModal from '../components/SaveToAlbumModal';
import './BrandStore.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrandStore = () => {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const { sessionToken } = useAuth();
  const [brand, setBrand] = useState(null);
  const [products, setProducts] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showTryonModal, setShowTryonModal] = useState(false);
  const [filters, setFilters] = useState({
    category: '',
    gender: '',
    search: ''
  });

  useEffect(() => {
    loadBrandDetails();
    loadProducts();
    if (sessionToken) {
      loadAlbums();
    }
  }, [brandId]);

  const loadBrandDetails = async () => {
    try {
      const response = await axios.get(`${API}/marketplace/brands/${brandId}`);
      if (response.data.success) {
        setBrand(response.data.brand);
      }
    } catch (error) {
      console.error('Error loading brand:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.gender) params.append('gender', filters.gender);
      if (filters.search) params.append('search', filters.search);
      
      const response = await axios.get(
        `${API}/marketplace/brands/${brandId}/products?${params.toString()}`
      );
      
      if (response.data.success) {
        setProducts(response.data.products);
      }
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const loadAlbums = async () => {
    try {
      const response = await axios.get(`${API}/albums`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      if (response.data.success) {
        const albumsWithPhotos = response.data.albums.filter(a => a.photos && a.photos.length > 0);
        setAlbums(albumsWithPhotos);
      }
    } catch (error) {
      console.error('Error loading albums:', error);
    }
  };

  const handleTryOn = (product) => {
    if (!sessionToken) {
      alert('Please login to try on products');
      navigate('/login');
      return;
    }
    
    if (albums.length === 0) {
      alert('Please create a model album first');
      navigate('/models');
      return;
    }
    
    setSelectedProduct(product);
    setShowTryonModal(true);
  };

  if (loading) {
    return <Layout><div className="loading">Loading brand...</div></Layout>;
  }

  if (!brand) {
    return <Layout><div className="error">Brand not found</div></Layout>;
  }

  return (
    <Layout>
      <div className="brand-store">
        <div className="brand-header">
          <button className="back-btn" onClick={() => navigate('/marketplace')}>
            ← Back to Marketplace
          </button>
          
          <div className="brand-hero">
            {brand.logo && <img src={brand.logo} alt={brand.name} className="brand-logo-large" />}
            <h1>{brand.name}</h1>
            <p>{brand.description}</p>
            <div className="brand-info">
              <span>📍 {brand.city}, {brand.country}</span>
              {brand.website && (
                <a href={brand.website} target="_blank" rel="noopener noreferrer">
                  🌐 Website
                </a>
              )}
              {brand.instagram && (
                <a href={brand.instagram} target="_blank" rel="noopener noreferrer">
                  📸 Instagram
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="filters-bar">
          <input
            type="text"
            placeholder="Search products..."
            value={filters.search}
            onChange={(e) => setFilters({...filters, search: e.target.value})}
          />
          <select
            value={filters.category}
            onChange={(e) => setFilters({...filters, category: e.target.value})}
          >
            <option value="">All Categories</option>
            {brand.categories?.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <select
            value={filters.gender}
            onChange={(e) => setFilters({...filters, gender: e.target.value})}
          >
            <option value="">All Genders</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="unisex">Unisex</option>
            <option value="kids">Kids</option>
          </select>
          <button className="btn-primary" onClick={loadProducts}>
            Filter
          </button>
        </div>

        <div className="products-section">
          <h2>Products ({products.length})</h2>
          
          {products.length === 0 ? (
            <div className="empty-state">
              <p>No products found</p>
            </div>
          ) : (
            <div className="store-products-grid">
              {products.map(product => (
                <div key={product.id} className="store-product-card">
                  <img src={product.picture_link} alt={product.product_name} />
                  <div className="product-details">
                    <h3>{product.product_name}</h3>
                    <p className="product-price">${product.price}</p>
                    <div className="product-tags">
                      <span>{product.size}</span>
                      <span>{product.gender}</span>
                      <span>{product.category}</span>
                    </div>
                    {product.product_link && (
                      <a 
                        href={product.product_link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="buy-link"
                      >
                        🛒 Buy Now
                      </a>
                    )}
                    <button 
                      className="tryon-btn"
                      onClick={() => handleTryOn(product)}
                    >
                      👔 Virtual Try-On
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {showTryonModal && selectedProduct && (
          <TryOnModal
            product={selectedProduct}
            albums={albums}
            sessionToken={sessionToken}
            onClose={() => setShowTryonModal(false)}
          />
        )}
      </div>
    </Layout>
  );
};

const TryOnModal = ({ product, albums, sessionToken, onClose }) => {
  const [selectedAlbum, setSelectedAlbum] = useState(albums[0]?.id || '');
  const [selectedPhoto, setSelectedPhoto] = useState('');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);

  useEffect(() => {
    if (selectedAlbum) {
      const album = albums.find(a => a.id === selectedAlbum);
      if (album?.photos?.length > 0) {
        setSelectedPhoto(album.photos[0].id);
      }
    }
  }, [selectedAlbum, albums]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/marketplace/tryon`,
        {
          product_id: product.id,
          album_id: selectedAlbum,
          photo_id: selectedPhoto
        },
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      
      if (response.data.success) {
        setResult(response.data.result_image);
        alert(`Try-on complete! Credits remaining: ${response.data.credits_remaining}`);
      }
    } catch (error) {
      console.error('Error generating try-on:', error);
      alert(error.response?.data?.detail || 'Failed to generate try-on');
    } finally {
      setGenerating(false);
    }
  };

  const album = albums.find(a => a.id === selectedAlbum);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <h2>Virtual Try-On: {product.product_name}</h2>
        
        {!result ? (
          <>
            <div className="form-group">
              <label>Select Model Album</label>
              <select
                value={selectedAlbum}
                onChange={(e) => setSelectedAlbum(e.target.value)}
              >
                {albums.map(album => (
                  <option key={album.id} value={album.id}>
                    {album.name} ({album.photos?.length || 0} photos)
                  </option>
                ))}
              </select>
            </div>
            
            {album?.photos && (
              <div className="photo-selector">
                <label>Select Photo</label>
                <div className="photos-grid">
                  {album.photos.map(photo => (
                    <div
                      key={photo.id}
                      className={`photo-option ${selectedPhoto === photo.id ? 'selected' : ''}`}
                      onClick={() => setSelectedPhoto(photo.id)}
                    >
                      <img
                        src={photo.image_base64.startsWith('data:') ? photo.image_base64 : `data:image/jpeg;base64,${photo.image_base64}`}
                        alt="Model"
                      />
                      {selectedPhoto === photo.id && <div className="selected-badge">✓</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="modal-actions">
              <button className="btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleGenerate}
                disabled={generating || !selectedPhoto}
              >
                {generating ? 'Generating...' : 'Generate Try-On (1 credit)'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="result-image">
              <img src={result} alt="Try-on result" />
            </div>
            
            <div className="result-product-info">
              <h3>{product.product_name}</h3>
              <p className="brand-name">{product.brand_name || 'Brand'}</p>
              <p className="product-price">${product.price}</p>
              
              {product.product_link && (
                <a 
                  href={product.product_link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="buy-now-btn"
                >
                  🛒 Buy Now
                </a>
              )}
            </div>
            
            <div className="result-actions">
              <button className="action-btn" onClick={() => setShowShareModal(true)}>
                📤 Share
              </button>
              <button className="action-btn" onClick={() => setShowSaveModal(true)}>
                💾 Save to Album
              </button>
              <button className="action-btn secondary" onClick={() => setResult(null)}>
                🔄 Try Another
              </button>
            </div>
            
            <button className="close-result-btn" onClick={onClose}>
              Close
            </button>
            
            {showShareModal && (
              <ShareModal
                isOpen={showShareModal}
                imageUrl={result}
                onClose={() => setShowShareModal(false)}
              />
            )}
            
            {showSaveModal && (
              <SaveToAlbumModal
                imageData={{ result_image: result, product: product }}
                sessionToken={sessionToken}
                onClose={() => setShowSaveModal(false)}
                onSuccess={() => {
                  alert('✅ Saved to album!');
                  setShowSaveModal(false);
                }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BrandStore;